import { ReactNode, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { SN_Navbar } from "@/components/SN_Navbar";
import { SndFooter } from "@/platform/chrome";
import { Crumbs, Faq, Label, W } from "@/platform/ui";
import { supabase } from "@/integrations/supabase/client";
import { TestimonialAvatar } from "@/components/testimonials/TestimonialAvatar";

// The offer kit (26 Oct 2026): one template for every page that sells a plan, a service or a call,
// set in the data centre's grammar (Terminal white, mono labels, panels, hairlines, tables).
// Plan of record: output/site-pages/OLD_PAGES_REDESIGN_PLAN_2026-10-26.md (vault).
// Buy, book and sign-in buttons are never built here: each page passes its existing button in as a
// child, wired exactly as before, so the kit only ever changes how a button is framed.

export { Faq };

/** The page frame: the site bar, the Terminal white ground, the compact data footer. */
export const OfferShell = ({ children }: { children: ReactNode }) => (
  <div className="snd min-h-screen" data-dir="terminal">
    <SN_Navbar />
    <main className="pb-14 pt-[56px] lg:pt-[60px]">{children}</main>
    <SndFooter />
  </div>
);

/** Crumbs, a mono label, the plain name, one line for whom; an optional panel on the right. */
export const OfferHeader = ({
  crumbs,
  label,
  title,
  lead,
  children,
  aside,
}: {
  crumbs: [to: string | null, label: string][];
  label: string;
  title: ReactNode;
  lead?: ReactNode;
  children?: ReactNode;
  aside?: ReactNode;
}) => (
  <header className="border-b border-[var(--sn-border)]" style={{ background: "var(--snd-hero)" }}>
    <W className="pb-7 pt-7">
      <Crumbs items={crumbs} />
      <div className={cn("mt-5 grid gap-6", aside && "lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] lg:items-start lg:gap-10")}>
        <div className="min-w-0">
          <Label className="font-mono text-[11.5px] uppercase tracking-[0.08em]">{label}</Label>
          <h1 className="mt-3 text-[clamp(28px,3.4vw,40px)] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground">{title}</h1>
          {lead && <div className="mt-3 max-w-[64ch] text-[15px] leading-[1.6] text-[var(--sn-fg2)]">{lead}</div>}
          {children}
        </div>
        {aside}
      </div>
    </W>
  </header>
);

/** The facts row under the header, as the company page's DES row: a small label above a mono value. */
export const OfferFacts = ({ items, className }: { items: { label: string; value: ReactNode; foot?: ReactNode }[]; className?: string }) => (
  <div
    className={cn(
      // an odd last fact spans the phone's two columns, so no empty grey box is left beside it
      "grid grid-cols-2 gap-px overflow-hidden rounded-[3px] border border-[var(--sn-border)] bg-[var(--sn-border)] sm:grid-cols-3 [&>*:last-child:nth-child(odd)]:col-span-2 sm:[&>*:last-child:nth-child(odd)]:col-span-1",
      items.length >= 6 ? "lg:grid-cols-6" : items.length === 5 ? "lg:grid-cols-5" : items.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4",
      className,
    )}
  >
    {items.map((k) => (
      <div key={k.label} className="min-w-0 bg-[hsl(var(--card))] px-4 py-3">
        <p className="text-[12px] text-muted-foreground">{k.label}</p>
        <p className="mt-1 truncate font-mono text-[15px] font-medium text-foreground [font-variant-numeric:tabular-nums]">{k.value}</p>
        {k.foot && <p className="mt-1 text-[11.5px] leading-[1.4] text-muted-foreground">{k.foot}</p>}
      </div>
    ))}
  </div>
);

/** A section: a hairline, a plain heading, an optional lead and a right-hand slot. */
export const OfferSec = ({ id, title, lead, right, children, className }: { id?: string; title: ReactNode; lead?: ReactNode; right?: ReactNode; children: ReactNode; className?: string }) => (
  // min-w-0: a section in a grid column shrinks to the column, so a wide table scrolls inside its frame on a phone
  <section id={id} className={cn("min-w-0 scroll-mt-[120px] pt-9", className)}>
    <div className="mb-4 flex flex-wrap items-end justify-between gap-x-8 gap-y-2 border-t border-[var(--sn-border-strong)] pt-4">
      <div className="min-w-0">
        <h2 className="text-[19px] font-semibold leading-[1.2] tracking-[-0.01em] text-foreground">{title}</h2>
        {lead && <div className="mt-1.5 max-w-[86ch] text-[13.5px] leading-[1.55] text-muted-foreground">{lead}</div>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

/** One price inside a price panel: what it is, the figure, its unit and the billing note.
 * `pre` is a word printed small before the figure ("Starts from"), so a price string reads as it does today. */
export type PriceCell = { label: ReactNode; price: string; pre?: string; unit?: string; note?: ReactNode };

/** A plan: its name in the band, one or more prices side by side, what it includes, then the
 * existing buy button passed in as `action`, untouched, and any discovery-call link as `foot`. */
export const PricePanel = ({
  title,
  right,
  cells,
  includes,
  action,
  foot,
  lead = false,
  className,
}: {
  title: ReactNode;
  right?: ReactNode;
  cells: PriceCell[];
  includes?: ReactNode[];
  action: ReactNode;
  foot?: ReactNode;
  /** The plan most readers take: a 2px orange rule on top, as the selected tab carries. */
  lead?: boolean;
  className?: string;
}) => (
  <div className={cn("flex min-w-0 flex-col overflow-hidden rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))]", lead && "border-t-2 border-t-[var(--sn-brand)]", className)}>
    <div className="flex items-center justify-between gap-3 border-b border-[var(--sn-border)] bg-[var(--snd-band)] px-4 py-2">
      <div className="text-[13px] font-semibold text-foreground">{title}</div>
      {right && <div className="font-mono text-[11.5px] text-muted-foreground">{right}</div>}
    </div>
    <div className={cn("grid divide-[var(--sn-border)]", cells.length > 1 ? "grid-cols-2 divide-x" : "grid-cols-1")}>
      {cells.map((c, i) => (
        <div key={i} className="min-w-0 px-4 py-4">
          <p className="text-[12px] text-muted-foreground">{c.label}</p>
          <p className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5">
            {c.pre && <span className="text-[13px] text-muted-foreground">{c.pre}</span>}
            <span className="text-[32px] font-semibold leading-none tracking-[-0.03em] text-foreground [font-variant-numeric:tabular-nums]">{c.price}</span>
            {c.unit && <span className="text-[13px] text-muted-foreground">/ {c.unit}</span>}
          </p>
          {c.note && <p className="mt-2 text-[12.5px] leading-[1.5] text-muted-foreground">{c.note}</p>}
        </div>
      ))}
    </div>
    {includes && includes.length > 0 && (
      <ul className="flex-1 border-t border-[var(--sn-border)] px-4 py-1">
        {includes.map((line, i) => (
          <li key={i} className="flex gap-2.5 border-t border-[var(--sn-border)] py-2 text-[13.5px] leading-[1.5] text-[var(--sn-fg2)] first:border-t-0">
            <span aria-hidden className="mt-[7px] h-[5px] w-[5px] shrink-0 bg-[var(--sn-fg2)] opacity-50" />
            <span>{line}</span>
          </li>
        ))}
      </ul>
    )}
    <div className="border-t border-[var(--sn-border)] px-4 py-3.5">
      {action}
      {foot && <div className="mt-2.5 text-[12.5px] text-muted-foreground">{foot}</div>}
    </div>
  </div>
);

/** The kit's two button looks, for a page's existing buttons: the one solid orange action and the plain one. */
export const offerBtn = {
  solid: "inline-flex h-10 w-full items-center justify-center gap-2 rounded-[3px] bg-primary px-[18px] text-[14px] font-semibold text-primary-foreground transition hover:brightness-110 disabled:opacity-70",
  plain: "inline-flex h-10 w-full items-center justify-center gap-2 rounded-[3px] border border-[var(--sn-border-strong)] bg-[hsl(var(--card))] px-[18px] text-[14px] font-semibold text-foreground transition-colors hover:border-foreground",
};

/** What is included: a table of item, what you get and a third column (how often, or which plan).
 * Give a two-item head for a plain two-column table when the offer states no frequency. */
export const IncludedTable = ({ head = ["Item", "What you get", "How often"], rows }: { head?: [string, string] | [string, string, string]; rows: ([ReactNode, ReactNode] | [ReactNode, ReactNode, ReactNode])[] }) => (
  <div className="overflow-x-auto rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))]">
    <table className={cn("w-full border-collapse text-[13.5px]", head.length === 3 ? "min-w-[640px]" : "min-w-[520px]")}>
      <thead>
        <tr className="border-b border-[var(--sn-border-strong)] bg-[var(--snd-band)] text-left text-[12px] text-[var(--sn-fg2)]">
          <th className={cn("px-4 py-2 font-medium", head.length === 3 ? "w-[24%]" : "w-[32%]")}>{head[0]}</th>
          <th className="px-3 py-2 font-medium">{head[1]}</th>
          {head.length === 3 && <th className="w-[16%] px-4 py-2 text-right font-medium">{head[2]}</th>}
        </tr>
      </thead>
      <tbody>
        {rows.map(([a, b, c], i) => (
          <tr key={i} className="border-b border-[var(--sn-border)] align-top last:border-b-0">
            <td className="px-4 py-2.5 font-medium text-foreground">{a}</td>
            <td className="px-3 py-2.5 leading-[1.55] text-[var(--sn-fg2)]">{b}</td>
            {head.length === 3 && <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-[12px] text-muted-foreground">{c}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** A schedule or curriculum: when, what, and the detail, one row per week or session. */
export const ScheduleTable = ({ head = ["When", "Session", "What it covers"], rows }: { head?: [string, string, string]; rows: [ReactNode, ReactNode, ReactNode][] }) => (
  <div className="overflow-x-auto rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))]">
    <table className="w-full min-w-[640px] border-collapse text-[13.5px]">
      <thead>
        <tr className="border-b border-[var(--sn-border-strong)] bg-[var(--snd-band)] text-left text-[12px] text-[var(--sn-fg2)]">
          <th className="w-[14%] px-4 py-2 font-medium">{head[0]}</th>
          <th className="w-[30%] px-3 py-2 font-medium">{head[1]}</th>
          <th className="px-4 py-2 font-medium">{head[2]}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([a, b, c], i) => (
          <tr key={i} className="border-b border-[var(--sn-border)] align-top last:border-b-0">
            <td className="whitespace-nowrap px-4 py-2.5 font-mono text-[12.5px] text-muted-foreground">{a}</td>
            <td className="px-3 py-2.5 font-medium text-foreground">{b}</td>
            <td className="px-4 py-2.5 leading-[1.55] text-[var(--sn-fg2)]">{c}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export type Quote = { id: string; full_name: string; role_title: string; company: string; headline: string; headshot_url: string | null };

/** The testimonials the admin flags for the home page, read the same way the old section read them. */
export const useFeaturedQuotes = (n = 3) => {
  const [rows, setRows] = useState<Quote[] | null>(null);
  useEffect(() => {
    let ignore = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_featured_testimonials");
      if (ignore) return;
      setRows(error ? [] : ((data as Quote[]) || []).slice(0, n));
    })();
    return () => { ignore = true; };
  }, [n]);
  return rows;
};

/** Proof as one compact panel of quotes, never a wall. Hides itself when there are none. */
export const QuotePanel = ({ title = "Members", rows, className }: { title?: ReactNode; rows: Quote[] | null; className?: string }) => {
  if (!rows || rows.length === 0) return null;
  return (
    <div className={cn("min-w-0 overflow-hidden rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))]", className)}>
      <div className="flex items-center justify-between gap-3 border-b border-[var(--sn-border)] bg-[var(--snd-band)] px-4 py-2">
        <div className="text-[13px] font-semibold text-foreground">{title}</div>
        <Link to="/testimonials" className="text-[12.5px] text-[var(--sn-fg2)] hover:text-foreground hover:underline">View all testimonials</Link>
      </div>
      <ul className="divide-y divide-[var(--sn-border)]">
        {rows.map((q) => (
          <li key={q.id} className="px-4 py-3.5">
            <blockquote className="m-0 text-[14.5px] font-medium leading-[1.45] text-foreground">&ldquo;{q.headline}&rdquo;</blockquote>
            <Link to={`/testimonials#t-${q.id}`} className="group mt-2 flex items-center gap-2.5">
              <TestimonialAvatar name={q.full_name} src={q.headshot_url} size={24} />
              <span className="min-w-0 truncate text-[12.5px] text-muted-foreground">
                <span className="text-foreground group-hover:underline">{q.full_name}</span> · {q.role_title} at {q.company}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Any other table on an offer page (the services catalogue, the tool stack, the founding wall): a head row,
 * optional group rows that span the table, and per-column widths and alignment. */
export type OfferRow = { group: ReactNode } | { cells: ReactNode[]; key?: string };
export const OfferTable = ({ head, rows, widths = [], right = [], minW = 640, className }: { head: ReactNode[]; rows: OfferRow[]; widths?: string[]; right?: number[]; minW?: number; className?: string }) => (
  <div className={cn("overflow-x-auto rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))]", className)}>
    <table className="w-full border-collapse text-[13.5px]" style={{ minWidth: minW }}>
      <thead>
        <tr className="border-b border-[var(--sn-border-strong)] bg-[var(--snd-band)] text-left text-[12px] text-[var(--sn-fg2)]">
          {head.map((h, i) => (
            <th key={i} className={cn("px-3 py-2 font-medium first:pl-4 last:pr-4", right.includes(i) && "text-right")} style={widths[i] ? { width: widths[i] } : undefined}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) =>
          "group" in r ? (
            <tr key={i} className="border-b border-[var(--sn-border)] bg-[var(--snd-wash)]">
              <td colSpan={head.length} className="px-4 py-1.5 font-mono text-[11.5px] uppercase tracking-[0.08em] text-muted-foreground">{r.group}</td>
            </tr>
          ) : (
            <tr key={r.key ?? i} className="border-b border-[var(--sn-border)] align-top last:border-b-0">
              {r.cells.map((c, j) => (
                <td key={j} className={cn("px-3 py-2.5 leading-[1.55] first:pl-4 last:pr-4", j === 0 ? "text-foreground" : "text-[var(--sn-fg2)]", right.includes(j) && "text-right")}>{c}</td>
              ))}
            </tr>
          ),
        )}
      </tbody>
    </table>
  </div>
);

/** Quotes written into a page (name, date, words), as one compact panel: the first few open, the rest one click away. */
export type PageQuote = { name: string; date?: string; quote: string };
export const QuoteList = ({ title = "Participants", rows, open = 4, className }: { title?: ReactNode; rows: PageQuote[]; open?: number; className?: string }) => {
  const item = (q: PageQuote, i: number) => (
    <li key={i} className="px-4 py-3">
      <blockquote className="m-0 text-[14px] leading-[1.5] text-foreground">&ldquo;{q.quote}&rdquo;</blockquote>
      <p className="mt-1.5 font-mono text-[11.5px] uppercase tracking-[0.06em] text-muted-foreground">{q.name}{q.date && <span className="normal-case tracking-normal"> · {q.date}</span>}</p>
    </li>
  );
  return (
    <div className={cn("min-w-0 overflow-hidden rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))]", className)}>
      <div className="flex items-center justify-between gap-3 border-b border-[var(--sn-border)] bg-[var(--snd-band)] px-4 py-2">
        <div className="text-[13px] font-semibold text-foreground">{title}</div>
        <span className="font-mono text-[11.5px] text-muted-foreground">{rows.length}</span>
      </div>
      <ul className="divide-y divide-[var(--sn-border)]">{rows.slice(0, open).map(item)}</ul>
      {rows.length > open && (
        <details className="group border-t border-[var(--sn-border)]">
          <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-2.5 text-[13px] font-medium text-foreground hover:bg-[var(--snd-wash)]">
            <span className="group-open:hidden">Show all {rows.length}</span>
            <span className="hidden group-open:inline">Show fewer</span>
            <span className="font-mono text-muted-foreground transition-transform group-open:rotate-45">+</span>
          </summary>
          <ul className="divide-y divide-[var(--sn-border)] border-t border-[var(--sn-border)]">{rows.slice(open).map((q, i) => item(q, i + open))}</ul>
        </details>
      )}
    </div>
  );
};

/** Longer material kept on the page but folded: a summary line that opens the rest. */
export const Fold = ({ summary, children, className }: { summary: ReactNode; children: ReactNode; className?: string }) => (
  <details className={cn("group rounded-[3px] border border-[var(--sn-border)] bg-[hsl(var(--card))]", className)}>
    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3 text-[14px] font-medium text-foreground hover:bg-[var(--snd-wash)]">
      {summary}
      <span className="font-mono text-muted-foreground transition-transform group-open:rotate-45">+</span>
    </summary>
    <div className="border-t border-[var(--sn-border)] px-4 py-4">{children}</div>
  </details>
);

/** Refund, cancellation and the licence in one line, with any short note in front. */
export const TermsLine = ({ note, links, right }: { note?: ReactNode; links: [to: string, label: string][]; right?: ReactNode }) => (
  <div className="mt-8 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 border-t border-[var(--sn-border)] pt-4 text-[12.5px] leading-[1.55] text-muted-foreground">
    <p className="m-0 max-w-[90ch]">
      {note}
      {note && " "}
      {links.map(([to, label], i) => (
        <span key={to}>
          {i > 0 && <span className="px-1.5 opacity-60">·</span>}
          <Link to={to} className="text-[var(--sn-fg2)] underline decoration-[var(--sn-border-strong)] underline-offset-4 hover:text-foreground">{label}</Link>
        </span>
      ))}
    </p>
    {right}
  </div>
);
